import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  output,
  SimpleChanges,
  TemplateRef,
  viewChild
} from '@angular/core';
import {Html5Qrcode, Html5QrcodeCameraScanConfig, Html5QrcodeSupportedFormats} from 'html5-qrcode';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {
  CdkDynamicGroupModule,
  CdkDynamicTable,
  CdkDynamicTableModule,
  CdkDynamicTableService,
  DgPaginatorModule,
  DgTableModule
} from '@datagrupo2/dg-ng-util';
import {distinctUntilChanged, identity, Subject, throttleTime} from 'rxjs';
import {MatIcon} from "@angular/material/icon";
import {
  CustomLoaderTableComponent
} from "../../../shared/custom-components/custom-loader-table/custom-loader-table.component";
import {NgTemplateOutlet} from "@angular/common";
import {COOLDOWN_PADRAO_MS, deveEmitir, type UltimaLeitura} from './scan-gate';

@Component({
  selector: 'app-qr-scanner',
  templateUrl: './qr-scanner.component.html',
  styleUrls: ['./qr-scanner.component.scss'],
  imports: [
    MatIcon,
    CdkDynamicGroupModule,
    ReactiveFormsModule,
    CdkDynamicTableModule,
    CustomLoaderTableComponent,
    DgPaginatorModule,
    DgTableModule,
    NgTemplateOutlet
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QrScannerComponent<T = unknown> implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private createTable = inject(CdkDynamicTableService);
  private cdr = inject(ChangeDetectorRef);

  @Input() isEmbedded: boolean = false;
  readonly showHistory = input<boolean>(true);
  readonly showTopBar = input<boolean>(true);
  @Input() title: string = '';
  readonly tableConfig = input<CdkDynamicTable.createDynamicTable | null>(null); // Configuração externa da tabela
  readonly keepCameraOpen = input<boolean>(false); // Se true, não fecha a câmera após scan
  readonly hideControls = input<boolean>(false); // Se true, esconde os botões de controle (para modo wizard/stepper)
  readonly scanSuccess = output<string>();
  readonly closeScanner = output<void>();
  readonly codeFormat = input<Html5QrcodeSupportedFormats>(Html5QrcodeSupportedFormats.QR_CODE
/**
 * Duração do throttle em milissegundos
 */
);

  /**
   * Duração do throttle em milissegundos
   */
  readonly throttleDuration = input<number>(500);

  /**
   *
   */
  readonly skipDuplicates = input<boolean>(false);

  /**
   * Janela em que o MESMO código não é reemitido. Ver `scan-gate.ts`: sem isto
   * um QR parado na frente da lente vira uma emissão a cada 500ms, e cada
   * emissão vira um POST de check-in.
   */
  readonly cooldownMs = input<number>(COOLDOWN_PADRAO_MS);

  // Slots para conteúdo customizado
  @ContentChild('headerContent') headerContent?: TemplateRef<unknown>;
  @ContentChild('tableHeaderContent') tableHeaderContent?: TemplateRef<unknown>;
  @ContentChild('historicoListItem') historicoListItem?: TemplateRef<{ $implicit: T }>;
  @ContentChild('bottomNavContent') bottomNavContent?: TemplateRef<unknown>;

  qrCodeScanner: Html5Qrcode | null = null;
  isScanning: boolean = false;
  errorMessage: string = '';
  hasCameraError: boolean = false; // Flag para detectar erro de câmera
  zoomLevel: number = 1;
  flashEnabled: boolean = false;
  currentFacingMode: 'environment' | 'user' = 'environment'; // Default: câmera traseira
  ultimoCodigoLido: string = '';
  showHistorico: boolean = false;

  // Flags para verificar suporte de features do dispositivo
  supportsFlash: boolean = false;
  supportsZoom: boolean = false;
  supportsMultipleCameras: boolean = false;
  isMirrored: boolean = false; // Controle de espelhamento da câmera

  // Modo de entrada: 'scan' ou 'digitar'
  modoEntrada: 'scan' | 'digitar' = 'scan';
  showInputDialog: boolean = false;
  codigoManual = new FormControl('');

  // Histórico - sempre vem de tableConfig
  table: CdkDynamicTable.tableClass | undefined;
  filtroHistorico = new FormControl('');
  historicoItems: T[] = [];
  carregandoMaisItens: boolean = false;
  readonly historicoListElement = viewChild.required<ElementRef>('historicoList');

  // Listener de resize
  private resizeListener?: () => void;
  private resizeTimeout?: any;

  private scanSucessSubject = new Subject<string>();
  private ultimaLeitura: UltimaLeitura | null = null;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.scanSucessSubject
    .pipe(throttleTime(this.throttleDuration()),this.skipDuplicates() ? distinctUntilChanged() : identity)
    .subscribe((codigo: string) => {
      const agora = Date.now();
      if (!deveEmitir(codigo, this.ultimaLeitura, agora, this.cooldownMs())) return;
      this.ultimaLeitura = { codigo, em: agora };
      this.ultimoCodigoLido = codigo;
      this.scanSuccess.emit(codigo);
    });
  }

  ngOnInit(): void {
    // A tabela sempre vem de tableConfig
    const showHistory = this.showHistory();
    if (this.tableConfig() && showHistory) {
      this.inicializarHistoricoExterno();
    } else if (!showHistory) {
      this.table = undefined;
    }

    // Carregar estado de espelhamento do sessionStorage (apenas em modo embedded)
    if (this.isEmbedded) {
      this.carregarEstadoEspelhamento();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se tableConfig mudar e estiver em modo embutido, reinicializar
    if (changes['tableConfig'] && this.isEmbedded && this.tableConfig() && this.showHistory()) {
      if (this.table) {
        this.table.destroy();
      }
      this.inicializarHistoricoExterno();
    }
  }

  ngAfterViewInit(): void {
    // Bloquear scroll e zoom do body quando o scanner estiver aberto (apenas se não for embedded)
    if (!this.isEmbedded) {
    this.blockBodyScroll();
    }

    // Aguardar um pouco para garantir que o DOM está pronto
    // No modo embedded, aumentar o delay para garantir que o container está totalmente renderizado
    const delay = this.isEmbedded ? 500 : 300;
    setTimeout(() => {
      this.iniciarScanner();
      this.setupResizeListener();
    }, delay);
  }

  ngOnDestroy(): void {
    this.scanSucessSubject.unsubscribe();
    this.pararScanner();
    this.removeResizeListener();
    // Restaurar scroll e zoom do body quando o scanner for fechado (apenas se não for embedded)
    if (!this.isEmbedded) {
    this.restoreBodyScroll();
    }
  }

  private scrollPosition: { x: number; y: number } = { x: 0, y: 0 };

  private blockBodyScroll(): void {
    const body = document.body;
    const html = document.documentElement;

    // Salvar posição atual do scroll
    this.scrollPosition = {
      x: window.scrollX || window.pageXOffset || 0,
      y: window.scrollY || window.pageYOffset || 0
    };

    // Bloquear scroll
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.height = '100%';
    body.style.top = `-${this.scrollPosition.y}px`;
    body.style.left = `-${this.scrollPosition.x}px`;
    body.style.touchAction = 'none';

    // Bloquear scroll no html também
    html.style.overflow = 'hidden';
    html.style.touchAction = 'none';

    // Adicionar classe ao body para facilitar estilização
    body.classList.add('qr-scanner-open');

    // Prevenir zoom com pinch através de event listeners
    document.addEventListener('touchstart', this.preventPinchZoom, { passive: false });
    document.addEventListener('touchmove', this.preventPinchZoom, { passive: false });
    document.addEventListener('touchend', this.preventPinchZoom, { passive: false });
    document.addEventListener('gesturestart', this.preventPinchZoom, { passive: false });
    document.addEventListener('gesturechange', this.preventPinchZoom, { passive: false });
    document.addEventListener('gestureend', this.preventPinchZoom, { passive: false });
  }

  private restoreBodyScroll(): void {
    const body = document.body;
    const html = document.documentElement;

    // Restaurar estilos do body
    body.style.overflow = '';
    body.style.position = '';
    body.style.width = '';
    body.style.height = '';
    body.style.top = '';
    body.style.left = '';
    body.style.touchAction = '';

    // Restaurar estilos do html
    html.style.overflow = '';
    html.style.touchAction = '';

    // Restaurar posição do scroll
    window.scrollTo(this.scrollPosition.x, this.scrollPosition.y);

    // Remover classe do body
    body.classList.remove('qr-scanner-open');

    // Remover event listeners
    document.removeEventListener('touchstart', this.preventPinchZoom);
    document.removeEventListener('touchmove', this.preventPinchZoom);
    document.removeEventListener('touchend', this.preventPinchZoom);
    document.removeEventListener('gesturestart', this.preventPinchZoom);
    document.removeEventListener('gesturechange', this.preventPinchZoom);
    document.removeEventListener('gestureend', this.preventPinchZoom);
  }

  private preventPinchZoom = (e: Event): void => {
    // Se houver mais de um toque, prevenir o comportamento padrão (pinch zoom)
    if (e.type.startsWith('touch')) {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches && touchEvent.touches.length > 1) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
    // Prevenir gestos de zoom (iOS)
    if (e.type.startsWith('gesture')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  setupResizeListener(): void {
    // Debounce para evitar múltiplas chamadas durante o resize
    this.resizeListener = () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      this.resizeTimeout = setTimeout(() => {
        this.ajustarVideoAspectRatio();
      }, 250);
    };
    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('orientationchange', this.resizeListener);
  }

  removeResizeListener(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      window.removeEventListener('orientationchange', this.resizeListener);
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  /**
   * Largura, em px, do "mundo" que o html5-qrcode enxerga.
   *
   * NÃO é decoração: é a RESOLUÇÃO DE LEITURA. O html5-qrcode cria o <video>
   * com `parentElement.clientWidth` (camera/core-impl.js) e, no evento
   * `playing`, congela a região de leitura e o canvas nesse tamanho
   * (`setupUi`). A cada quadro ele desenha o frame inteiro nesse canvas — ou
   * seja, o CSS do painel decide em que resolução o QR é decodificado.
   *
   * No painel embutido do desktop isso dava 378px, e um stream de 640px era
   * reamostrado para 378 antes do decodificador. A perda não é gradual:
   * medido na tela com o mesmo quadro parado, 640 decodifica e 600 já não.
   * Um QR pequeno vive na margem e qualquer reamostragem o joga para fora
   * dela. Era por isso que a câmera lia em tela cheia e NUNCA lia no painel
   * do desktop.
   *
   * 640x480 porque é o modo que praticamente toda webcam e câmera de celular
   * entrega, e é o que pedimos em `videoConstraints`. Ampliar (stream menor
   * que o mundo) não custa nada ao decodificador; reduzir é o que mata.
   *
   * ponytail: um valor só para todos os dispositivos. Se aparecer câmera cujo
   * modo nativo seja bem maior e o QR continue apertado, o passo seguinte é
   * ler `videoWidth` depois do primeiro `playing` e reiniciar o scanner com
   * esse tamanho — custa um restart na primeira abertura.
   */
  private readonly LARGURA_LEITURA = 640;
  private readonly ALTURA_LEITURA = 480;

  /**
   * Encaixa o mundo de `LARGURA_LEITURA` no painel via `transform: scale()`.
   *
   * `transform` não altera `clientWidth`, então o decodificador continua
   * vendo 640x480 enquanto o operador vê o painel do tamanho que for. O
   * espelhamento entra como `scale` negativo no eixo X, para não haver duas
   * transformações brigando pela mesma propriedade.
   */
  ajustarVideoAspectRatio(): void {
    const qrReader = document.getElementById('qr-reader');
    const area = qrReader?.parentElement;
    if (!qrReader || !area) return;

    // Tudo inline e `!important`: este bloco é resolução de leitura, não
    // estética, e não pode perder para a cascata. `.scanning-area` é flex, e
    // como flex item o elemento era ENCOLHIDO de volta para a largura do
    // painel mesmo com `width: 640px` — daí `position: absolute` e
    // `flex: none`, que o tiram do fluxo do flex.
    qrReader.style.setProperty('position', 'absolute', 'important');
    qrReader.style.setProperty('flex', 'none', 'important');
    qrReader.style.setProperty('top', '50%', 'important');
    qrReader.style.setProperty('left', '50%', 'important');
    qrReader.style.setProperty('width', `${this.LARGURA_LEITURA}px`, 'important');
    qrReader.style.setProperty('height', `${this.ALTURA_LEITURA}px`, 'important');

    const caixa = area.getBoundingClientRect();
    if (!caixa.width || !caixa.height) return;

    // `max` e não `min`: preenche o painel como o `object-fit: cover` fazia,
    // sobrando nas bordas em vez de deixar listras pretas.
    const escala = Math.max(
      caixa.width / this.LARGURA_LEITURA,
      caixa.height / this.ALTURA_LEITURA,
    );
    const espelho = this.isMirrored ? -1 : 1;

    qrReader.style.setProperty(
      'transform',
      `translate(-50%, -50%) scale(${escala * espelho}, ${escala})`,
      'important',
    );
  }

  iniciarScanner(): void {
    const qrReader = document.getElementById('qr-reader');

    if (!qrReader) {
      // No modo embedded, tentar novamente após um pequeno delay
      if (this.isEmbedded) {
        setTimeout(() => {
          this.iniciarScanner();
        }, 200);
        return;
      }
      this.errorMessage = 'Elemento do scanner não encontrado';
      this.hasCameraError = true;
      return;
    }

    if (this.qrCodeScanner) {
      this.pararScanner();
    }

    qrReader.innerHTML = '';
    this.errorMessage = '';
    this.hasCameraError = false; // Resetar erro ao tentar iniciar novamente

    // ANTES do start: o html5-qrcode lê `parentElement.clientWidth` para criar
    // o <video> e congela a região de leitura no primeiro `playing`. Ajustar
    // depois só encolheria a imagem, sem devolver resolução ao decodificador.
    this.ajustarVideoAspectRatio();

    this.qrCodeScanner = new Html5Qrcode('qr-reader', {
      formatsToSupport: [this.codeFormat()],
      verbose: false,
    } );

    const config:Html5QrcodeCameraScanConfig = {
      fps: 10,
      // Pedir o mesmo tamanho do mundo de leitura mantém a razão em 1:1. Se a
      // câmera entregar menos, o frame é AMPLIADO no canvas, o que não
      // atrapalha o decodificador — o que atrapalha é reduzir.
      //
      // Vai AQUI e não no primeiro argumento do `start`: aquele parâmetro é o
      // `cameraIdOrConfig` e aceita exatamente uma chave (`deviceId` OU
      // `facingMode`); mandar resolução junto derruba o scanner com
      // "should have exactly 1 key". E como `videoConstraints` SUBSTITUI as
      // constraints derivadas daquele argumento, o `facingMode` precisa
      // aparecer de novo aqui dentro.
      videoConstraints: {
        facingMode: this.currentFacingMode,
        width: { ideal: this.LARGURA_LEITURA },
        height: { ideal: this.ALTURA_LEITURA },
      },
      // qrbox: { width: 250, height: 250 },
      // Remover aspectRatio fixo para permitir que o vídeo se ajuste ao container
      // aspectRatio: 1.0,
      disableFlip: false,

    };

    this.isScanning = true;

    this.qrCodeScanner
      .start(
        { facingMode: this.currentFacingMode },
        config,
        (decodedText: string) => {
          // Código escaneado com sucesso

          this.scanSucessSubject.next(decodedText);
        },
        (errorMessage: string) => {
          // Erro ignorado - o scanner continua tentando
        }
      )
      .then(() => {
        // Resetar flag de erro se o scanner iniciar com sucesso
        this.hasCameraError = false;
        this.errorMessage = '';
        // Após o scanner iniciar, ajustar o aspect ratio do vídeo
        setTimeout(() => {
          this.ajustarVideoAspectRatio();
          // Verificar capabilities iniciais (flash, zoom, múltiplas câmeras) se possível
          this.checkCapabilities();
          // Aplicar espelhamento se estiver ativo (carregado do sessionStorage)
          if (this.isMirrored) {
            this.ajustarVideoAspectRatio();
          }
        }, 100);
      })
      .catch((err: any) => {
        console.error('Erro ao iniciar scanner:', err);
        this.isScanning = false;
        this.errorMessage =
          err?.message || 'Erro ao acessar a câmera. Verifique as permissões.';
        this.hasCameraError = true; // Marcar como erro de câmera
        this.qrCodeScanner = null;
      });
  }

  pararScanner(): void {
    if (this.qrCodeScanner && this.isScanning) {
      this.qrCodeScanner
        .stop()
        .then(() => {
          this.qrCodeScanner = null;
          this.isScanning = false;
          this.hasCameraError = false; // Resetar erro ao parar
        })
        .catch((err: any) => {
          // Ignorar erro se o scanner não estiver rodando
          if (err?.message?.includes('not running') || err?.message?.includes('not paused')) {
            // Scanner já estava parado, apenas limpar referência
            this.qrCodeScanner = null;
            this.isScanning = false;
            this.hasCameraError = false;
          } else {
            console.error('Erro ao parar scanner:', err);
            this.qrCodeScanner = null;
            this.isScanning = false;
            this.hasCameraError = false; // Resetar erro ao parar
          }
        });
    } else if (this.qrCodeScanner) {
      // Se existe scanner mas não está rodando, apenas limpar referência
      this.qrCodeScanner = null;
      this.isScanning = false;
      this.hasCameraError = false;
    }
  }

  getVideoTrack(): MediaStreamTrack | null {
    const qrReader = document.getElementById('qr-reader');
    if (!qrReader) return null;

    const video = qrReader.querySelector('video');
    if (!video || !video.srcObject) return null;

    const stream = video.srcObject as MediaStream;
    const tracks = stream.getVideoTracks();
    return tracks.length > 0 ? tracks[0] : null;
  }

  checkCapabilities(): void {
    const track = this.getVideoTrack();
    if (!track) return;

    const capabilities = track.getCapabilities ? track.getCapabilities() : {};
    const settings = track.getSettings ? track.getSettings() : {};

    // Verificar suporte a zoom
    // @ts-ignore
    const zoomCap = capabilities.zoom;
    // @ts-ignore
    this.supportsZoom = !!(zoomCap && (zoomCap.max > 1 || (zoomCap.min && zoomCap.min < zoomCap.max)));
    // @ts-ignore
    if (settings.zoom) {
      // @ts-ignore
      this.zoomLevel = settings.zoom;
    }

    // Verificar suporte a flash/torch
    // @ts-ignore
    this.supportsFlash = !!capabilities.torch;

    // Verificar suporte a múltiplas câmeras (após ter acesso à câmera)
    this.checkMultipleCamerasSupport();
  }

  async checkMultipleCamerasSupport(): Promise<void> {
    try {
      // Verificar se a API está disponível
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        this.supportsMultipleCameras = false;
        return;
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      // Se houver mais de uma câmera, suporta troca
      // Nota: alguns dispositivos podem listar a mesma câmera duas vezes com labels diferentes
      // então verificamos se há pelo menos 2 dispositivos distintos
      this.supportsMultipleCameras = videoDevices.length > 1;
    } catch (err) {
      console.warn('Erro ao verificar câmeras disponíveis:', err);
      // Em caso de erro, assumir que não suporta (mais seguro)
      this.supportsMultipleCameras = false;
    }
  }

  toggleFlash(): void {
    const track = this.getVideoTrack();
    if (!track) return;

    const capabilities = track.getCapabilities ? track.getCapabilities() : {};
    // @ts-ignore
    if (!capabilities.torch) {
      // Flash não suportado neste dispositivo/câmera
      // Tentar forçar mesmo assim caso getCapabilities não retorne tudo (alguns androids)
      // mas geralmente se não estiver aqui, não funciona.
      console.warn('Torch not supported');
    }

    this.flashEnabled = !this.flashEnabled;

    track.applyConstraints({
      advanced: [{ torch: this.flashEnabled } as any]
    }).catch(err => {
      console.error('Erro ao alterar flash:', err);
      // Reverter estado em caso de erro
      this.flashEnabled = !this.flashEnabled;
    });
  }

  switchCamera(): void {
    this.currentFacingMode = this.currentFacingMode === 'environment' ? 'user' : 'environment';
    this.pararScanner();

    // Pequeno delay para garantir que a câmera anterior foi liberada
    setTimeout(() => {
      this.iniciarScanner();
    }, 300);
  }

  toggleMirror(): void {
    this.isMirrored = !this.isMirrored;
    // Aplicar espelhamento imediatamente
    this.ajustarVideoAspectRatio();
    // Salvar estado no sessionStorage (apenas em modo embedded)
    if (this.isEmbedded) {
      this.salvarEstadoEspelhamento();
    }
    // Notificar mudanças para atualizar a UI (OnPush)
    this.cdr.markForCheck();
  }

  /**
   * Carrega o estado de espelhamento do sessionStorage
   */
  private carregarEstadoEspelhamento(): void {
    try {
      const estadoSalvo = sessionStorage.getItem('qr-scanner-mirrored');
      if (estadoSalvo !== null) {
        this.isMirrored = estadoSalvo === 'true';
      }
    } catch (error) {
      console.warn('Erro ao carregar estado de espelhamento do sessionStorage:', error);
    }
  }

  /**
   * Salva o estado de espelhamento no sessionStorage
   */
  private salvarEstadoEspelhamento(): void {
    try {
      sessionStorage.setItem('qr-scanner-mirrored', this.isMirrored.toString());
    } catch (error) {
      console.warn('Erro ao salvar estado de espelhamento no sessionStorage:', error);
    }
  }

  openGallery(): void {}

  toggleModoEntrada(): void {
    if (this.modoEntrada === 'scan') {
      // Mudar para modo digitar: parar câmera e abrir diálogo
      this.modoEntrada = 'digitar';
      if (this.isScanning) {
        this.pararScanner();
      }
      this.showInputDialog = true;
      this.codigoManual.setValue('');
    } else {
      // Mudar para modo scan: fechar diálogo e reiniciar câmera
      this.modoEntrada = 'scan';
      this.showInputDialog = false;
      this.codigoManual.setValue('');
      // Reiniciar scanner após um pequeno delay (só se não estiver já escaneando)
      if (!this.isScanning) {
        setTimeout(() => {
          this.iniciarScanner();
        }, 300);
      }
    }
  }

  confirmarCodigoManual(): void {
    const codigo = this.codigoManual.value?.trim();
    if (!codigo || codigo === '') {
      return;
    }

    // Fechar diálogo primeiro
    this.showInputDialog = false;
    this.modoEntrada = 'scan';
    this.codigoManual.setValue('');

    // Digitar é ato deliberado do operador: não passa pelo cooldown, que
    // existe só para o decodificador repetindo o mesmo quadro.
    this.ultimaLeitura = null;
    this.scanSucessSubject.next(codigo);

  }

  cancelarCodigoManual(): void {
    // Fechar diálogo e voltar para modo scan
    this.showInputDialog = false;
    this.modoEntrada = 'scan';
    this.codigoManual.setValue('');

    // Reiniciar scanner
    setTimeout(() => {
      this.iniciarScanner();
    }, 300);
  }

  onZoomChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.zoomLevel = parseFloat(target.value);

    const track = this.getVideoTrack();
    if (!track) return;

    const capabilities = track.getCapabilities ? track.getCapabilities() : {};

    // @ts-ignore
    if (capabilities.zoom) {
      track.applyConstraints({
        advanced: [{ zoom: this.zoomLevel } as any]
      }).catch(err => {
        console.error('Erro ao aplicar zoom:', err);
      });
    }
  }

  focusMode = ''

  focarManualmente(): void {
    const track = this.getVideoTrack();
    if (!track) return;

    const capabilities = track.getCapabilities ? track.getCapabilities() : {};
    // @ts-ignore
    const focusMode = capabilities.focusMode || [];
    this.focusMode = focusMode;

    // Tentar forçar o foco alternando modos ou definindo continuous
    // A maioria dos dispositivos móveis usa 'continuous' por padrão.
    // Para forçar um re-foco, podemos tentar mudar para 'manual' e voltar, ou reenviar 'continuous'.

    // @ts-ignore
    if (focusMode.includes('continuous')) {
      track.applyConstraints({
        advanced: [{ focusMode: 'continuous' } as any]
      })
      .then(() => {
        this.focusMode = 'continuous';
      })
      .catch(err => {
        this.focusMode = err;
        return console.warn('Erro ao focar:', err);
      });
    } else if (focusMode.includes('single-shot')) {
      track.applyConstraints({
        advanced: [{ focusMode: 'single-shot' } as any]
      }).then(() => {
        this.focusMode = 'single-shot';
      }).catch(err => {
        this.focusMode = err;
        return console.warn('Erro ao focar:', err);
      });
    }
  }

  onFileSelected(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (file) {
      this.pararScanner();

      setTimeout(() => {
        if (this.qrCodeScanner) {
          this.qrCodeScanner
            .scanFile(file, true)
            .then((result: string) => {
              this.scanSucessSubject.next(result);
              this.iniciarScanner();
            })
            .catch((err: any) => {
              console.error('Erro ao escanear arquivo:', err);
              this.iniciarScanner();
            });
        }
      }, 300);
    }
  }


  /**
   * Congela a leitura SEM soltar a câmera.
   *
   * `stop()` derruba o MediaStream e voltar dele custa segundos e uma nova
   * negociação de permissão em alguns navegadores — inviável entre um ingresso
   * e o próximo na fila. `pause(true)` mantém o stream e só desliga o decoder,
   * então `retomar()` é instantâneo.
   */
  public pausar(): void {
    try {
      this.qrCodeScanner?.pause(true);
    } catch {
      /* scanner ainda não iniciado ou já pausado — nada a congelar */
    }
  }

  public retomar(): void {
    try {
      this.qrCodeScanner?.resume();
    } catch {
      /* não estava pausado */
    }
  }

  // Métodos públicos para serem acessados via ViewChild pelo componente pai
  public iniciarScannerPublic(): void {
    this.iniciarScanner();
  }

  public pararScannerPublic(): void {
    this.pararScanner();
  }

  toggleHistorico(): void {
    this.showHistorico = !this.showHistorico;
    // Quando abrir o histórico, resetar itens e carregar primeira página
    if (this.showHistorico && this.table) {
      this.historicoItems = [];
      // Resetar para primeira página sem forçar sort específico (deixar tableConfig decidir)
      this.table.controls.pagination.set(
        { page: 0 },
        { find: false }
      );
      this.table.find();
    }
  }

  fecharHistorico(): void {
    this.showHistorico = false;
  }

  onHistoricoScroll(event: Event): void {
    // O scroll agora é na página inteira, então precisamos pegar o elemento correto
    const element = document.querySelector('.historico-mobile') as HTMLElement;
    if (!element) return;

    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;

    // Verificar se chegou ao final (com margem de 100px)
    if (
      scrollHeight - scrollTop - clientHeight < 100 &&
      !this.carregandoMaisItens
    ) {
      this.carregarMaisItens();
    }
  }

  carregarMaisItens(): void {
    if (!this.table || this.carregandoMaisItens) return;

    const pagination = this.table.controls.pagination.get();
    if (!pagination) return;

    const currentPage =
      typeof pagination.page === 'number' ? pagination.page : 0;
    const totalPages =
      typeof pagination.totalPages === 'number' ? pagination.totalPages : 0;

    // Verificar se há mais páginas
    if (currentPage < totalPages - 1) {
      this.carregandoMaisItens = true;
      const nextPage = currentPage + 1;
      this.table.controls.pagination.set(
        { page: nextPage, sort: ['data,desc'] },
        { find: true }
      );
    }
  }

  formatDate(date: string): string {
    if (!date) return '--';
    return new Date(date).toLocaleString('pt-br');
  }

  inicializarHistoricoExterno(): void {
    const tableConfig = this.tableConfig();
    if (!tableConfig) return;

    // Usar a configuração externa fornecida
    this.table = this.createTable.create('request', tableConfig);

    // Não abrir histórico automaticamente - o usuário deve clicar no botão
    // O histórico só será aberto quando o usuário clicar no botão de histórico

    // Subscrever às mudanças de dados para acumular itens (para mobile)
    this.table.observables.dataSource.subscribe((dataSource) => {
      if (dataSource && Array.isArray(dataSource)) {
        const pagination = this.table?.controls?.pagination?.get();
        const currentPage =
          typeof pagination?.page === 'number' ? pagination.page : 0;

        if (currentPage === 0) {
          // Primeira página, substituir itens
          this.historicoItems = [...dataSource];
        } else {
          // Páginas seguintes, acumular itens
          this.historicoItems = [...this.historicoItems, ...dataSource];
        }
        this.carregandoMaisItens = false;
      }
    });
  }
}
